import React, { useState, useRef, useEffect } from 'react';
import { Terminal, X, Send, Sparkles, ChevronRight, RefreshCw, Maximize2, Minimize2, Clock } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useI18n } from '../i18n';

/**
 * ChatInterface - The Oracle Chat Assistant
 * A floating terminal-style chat widget for club statistics and game rules
 */
const CHAT_SESSION_KEY = 'masaklubi_chat_session';
const CHAT_SESSION_ID_KEY = 'masaklubi_chat_session_id';
const CHAT_SESSION_TIMEOUT = 120 * 60 * 1000; // 120 minutes in ms

const getInitialMessages = (t) => [{
    role: 'oracle',
    text: t.chatInterface.welcome,
    type: 'WELCOME'
}];

// Generate or retrieve session ID
const getSessionId = () => {
    try {
        const saved = localStorage.getItem(CHAT_SESSION_ID_KEY);
        if (saved) {
            const sessionData = JSON.parse(saved);
            const now = Date.now();
            // Check if session is still valid
            if (sessionData.lastActivity && (now - sessionData.lastActivity) < CHAT_SESSION_TIMEOUT) {
                return sessionData.id;
            }
        }
        // Create new session ID
        const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(CHAT_SESSION_ID_KEY, JSON.stringify({ id: newId, lastActivity: Date.now() }));
        return newId;
    } catch (e) {
        console.error('Failed to get session ID:', e);
        return `session_${Date.now()}`;
    }
};

// Update session activity timestamp
const updateSessionActivity = (sessionId) => {
    try {
        localStorage.setItem(CHAT_SESSION_ID_KEY, JSON.stringify({ id: sessionId, lastActivity: Date.now() }));
    } catch (e) {
        console.error('Failed to update session activity:', e);
    }
};

// Load chat session from localStorage
const loadChatSession = (t) => {
    try {
        const saved = localStorage.getItem(CHAT_SESSION_KEY);
        if (saved) {
            const session = JSON.parse(saved);
            const now = Date.now();
            // Check if session is still valid (within timeout)
            if (session.lastActivity && (now - session.lastActivity) < CHAT_SESSION_TIMEOUT) {
                return session.messages || getInitialMessages(t);
            }
        }
    } catch (e) {
        console.error('Failed to load chat session:', e);
    }
    return getInitialMessages(t);
};

// Save chat session to localStorage
const saveChatSession = (messages) => {
    try {
        localStorage.setItem(CHAT_SESSION_KEY, JSON.stringify({
            messages,
            lastActivity: Date.now()
        }));
    } catch (e) {
        console.error('Failed to save chat session:', e);
    }
};

const ChatInterface = ({ user, onLaunchGame }) => {
    const { t } = useI18n(user?.club?.language);
    const [isOpen, setIsOpen] = useState(false);
    // Check if mobile - default to fullscreen on mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const [isFullscreen, setIsFullscreen] = useState(isMobile); // Start fullscreen on mobile
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [lastUserMessage, setLastUserMessage] = useState('');
    const [retryCountdown, setRetryCountdown] = useState(0);
    const [sessionId] = useState(() => getSessionId()); // Session ID for context
    const [keyboardOpen, setKeyboardOpen] = useState(false); // Track mobile keyboard state
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const chatWindowRef = useRef(null);

    useEffect(() => {
        setMessages(loadChatSession(t));
    }, [t]);

    // Save messages to localStorage whenever they change
    useEffect(() => {
        if (messages.length > 1) { // Don't save initial welcome only
            saveChatSession(messages);
        }
    }, [messages]);

    // Auto-scroll to bottom when messages update
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Handle mobile keyboard visibility
    const handleInputFocus = () => {
        if (isMobile) {
            setKeyboardOpen(true);
            // Scroll input into view after keyboard opens
            setTimeout(() => {
                inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
    };

    const handleInputBlur = () => {
        if (isMobile) {
            setKeyboardOpen(false);
        }
    };

    // Countdown timer effect
    useEffect(() => {
        if (retryCountdown > 0) {
            countdownTimerRef.current = setTimeout(() => {
                setRetryCountdown(prev => prev - 1);
            }, 1000);
        } else if (retryCountdown === 0 && countdownTimerRef.current) {
            // Timer just finished - trigger auto-retry
            clearTimeout(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
        
        return () => {
            if (countdownTimerRef.current) {
                clearTimeout(countdownTimerRef.current);
            }
        };
    }, [retryCountdown]);

    // Track if we should auto-retry when countdown finishes
    const shouldAutoRetryRef = useRef(false);

    // When countdown finishes, trigger auto-retry
    useEffect(() => {
        if (retryCountdown === 0 && shouldAutoRetryRef.current && lastUserMessage && !isLoading) {
            shouldAutoRetryRef.current = false;
            // Small delay to ensure state is clean
            setTimeout(() => {
                setMessages(prev => prev.slice(0, -1));
                sendMessage(lastUserMessage);
            }, 100);
        }
    }, [retryCountdown]);

    // Internal send function - handles the actual API call
    const sendMessageInternal = async (messageToSend, isRetry = false) => {
        if (!messageToSend || isLoading) return;

        // Clear any existing countdown
        setRetryCountdown(0);
        
        setLastUserMessage(messageToSend);
        setIsLoading(true);
        
        // Update session activity
        updateSessionActivity(sessionId);

        try {
            // Prepare conversation history for AI context (exclude welcome message)
            const conversationHistory = messages
                .filter(m => m.type !== 'WELCOME')
                .map(m => ({
                    role: m.role === 'user' ? 'user' : 'assistant',
                    content: m.text
                }));
            
            const response = await fetch('/api/oracle/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    message: messageToSend, 
                    sessionId,
                    history: conversationHistory 
                })
            });

            const data = await response.json();
            
            // Handle special actions
            if (data.specialAction === 'LAUNCH_TERMINAL_GAME') {
                setMessages(prev => [...prev, { 
                    role: 'oracle', 
                    text: data.text,
                    type: data.type
                }]);
                // Delay to show the message before launching game
                setTimeout(() => {
                    if (onLaunchGame) onLaunchGame();
                }, 2000);
            } else {
                // Regular response
                const oracleMessage = { 
                    role: 'oracle', 
                    text: data.text,
                    type: data.type,
                    chart: data.chart,
                    sources: data.sources,
                    cached: data.cached,
                    retry: data.retry,
                    retryDelay: data.retryDelay
                };
                
                setMessages(prev => [...prev, oracleMessage]);
                
                // Start countdown if retryDelay is present
                if (data.retryDelay && data.retryDelay > 0) {
                    shouldAutoRetryRef.current = true;
                    setRetryCountdown(data.retryDelay);
                }
            }
        } catch (error) {
            console.error('Oracle error:', error);
            setMessages(prev => [...prev, { 
                role: 'oracle', 
                text: t.chatInterface.connectionError,
                type: 'ERROR',
                retry: true
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    // Public send function - adds user message then calls internal
    const sendMessage = (retryMessage = null) => {
        const messageToSend = retryMessage || input.trim();
        if (!messageToSend || isLoading) return;

        if (!retryMessage) {
            setInput('');
            // Add user message only if not a retry
            setMessages(prev => [...prev, { role: 'user', text: messageToSend }]);
        }
        
        sendMessageInternal(messageToSend, !!retryMessage);
    };

    const handleRetry = () => {
        if (lastUserMessage && !isLoading) {
            // Cancel auto-retry and clear countdown
            shouldAutoRetryRef.current = false;
            setRetryCountdown(0);
            // Remove the last oracle message (the error/overloaded one)
            setMessages(prev => prev.slice(0, -1));
            sendMessage(lastUserMessage);
        }
    };

    // handleKeyPress moved inline to textarea onKeyDown

    // Chart colors
    const CHART_COLORS = ['#00ff88', '#58a6ff', '#f0b429', '#e74c3c', '#9b59b6', '#1abc9c', '#e67e22', '#3498db'];

    // Render chart based on type
    const renderChart = (chartData) => {
        if (!chartData || !chartData.data || chartData.data.length === 0) return null;

        const { type, data, xKey = 'name', yKeys = ['value'], yLabels = [t.chatInterface.chartValue] } = chartData;

        switch (type) {
            case 'bar':
                return (
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis dataKey={xKey} tick={{ fill: '#8b949e', fontSize: 11 }} />
                            <YAxis tick={{ fill: '#8b949e', fontSize: 11 }} />
                            <Tooltip 
                                contentStyle={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8 }}
                                labelStyle={{ color: '#c9d1d9' }}
                            />
                            {yKeys.map((key, idx) => (
                                <Bar key={key} dataKey={key} name={yLabels[idx] || key} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                            ))}
                            {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                        </BarChart>
                    </ResponsiveContainer>
                );

            case 'line':
                return (
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis dataKey={xKey} tick={{ fill: '#8b949e', fontSize: 11 }} />
                            <YAxis tick={{ fill: '#8b949e', fontSize: 11 }} />
                            <Tooltip 
                                contentStyle={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8 }}
                                labelStyle={{ color: '#c9d1d9' }}
                            />
                            {yKeys.map((key, idx) => (
                                <Line key={key} type="monotone" dataKey={key} name={yLabels[idx] || key} stroke={CHART_COLORS[idx % CHART_COLORS.length]} strokeWidth={2} dot={{ fill: CHART_COLORS[idx % CHART_COLORS.length] }} />
                            ))}
                            {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                        </LineChart>
                    </ResponsiveContainer>
                );

            case 'pie':
                return (
                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey={yKeys[0] || 'value'}
                                nameKey={xKey}
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                labelLine={{ stroke: '#8b949e' }}
                            >
                                {data.map((entry, idx) => (
                                    <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip 
                                contentStyle={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8 }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                );

            default:
                return null;
        }
    };

    const formatMessage = (msg) => {
        // Split by newlines and format
        return msg.text.split('\n').map((line, i) => (
            <span key={i}>
                {line}
                {i < msg.text.split('\n').length - 1 && <br />}
            </span>
        ));
    };

    return (
        <>
            {/* Floating Action Button */}
            <button 
                className={`oracle-fab ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title={t.chatInterface.fabTitle}
            >
                {isOpen ? (
                    <X size={24} />
                ) : (
                    <>
                        <Terminal size={24} />
                        <span className="oracle-fab-pulse"></span>
                    </>
                )}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div 
                    ref={chatWindowRef}
                    className={`oracle-chat-window ${isFullscreen ? 'fullscreen' : ''} ${keyboardOpen ? 'keyboard-open' : ''}`}
                >
                    {/* Header */}
                    <div className="oracle-chat-header">
                        <div className="oracle-header-title">
                            <Terminal size={18} />
                            <span>{t.chatInterface.title}</span>
                            <Sparkles size={14} className="oracle-sparkle" />
                        </div>
                        <div className="oracle-header-actions">
                            <button 
                                className="oracle-header-btn"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                title={isFullscreen ? t.chatInterface.shrink : t.chatInterface.fullscreen}
                            >
                                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                            </button>
                            <button 
                                className="oracle-header-btn oracle-close-btn"
                                onClick={() => { setIsOpen(false); setIsFullscreen(false); }}
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Messages Container */}
                    <div className="oracle-messages">
                        {messages.map((msg, idx) => (
                            <div 
                                key={idx} 
                                className={`oracle-message ${msg.role}`}
                            >
                                {msg.role === 'oracle' && (
                                    <div className="oracle-message-header">
                                        <ChevronRight size={14} />
                                        <span>{t.chatInterface.oracle}</span>
                                        {msg.cached && (
                                            <span className="oracle-cached-badge">{t.chatInterface.cached}</span>
                                        )}
                                    </div>
                                )}
                                <div className="oracle-message-content">
                                    {formatMessage(msg)}
                                </div>
                                
                                {/* Chart Visualization */}
                                {msg.chart && msg.chart.data && (
                                    <div className="oracle-chart-container">
                                        <div className="oracle-chart-title">
                                            📊 {msg.chart.title || t.chatInterface.chartTitle}
                                        </div>
                                        <div className="oracle-chart">
                                            {renderChart(msg.chart)}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Source Links */}
                                {msg.sources && msg.sources.length > 0 && (
                                    <div className="oracle-sources">
                                        {msg.sources.map((src, i) => (
                                            <a 
                                                key={i}
                                                href={src.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="oracle-source-link"
                                            >
                                                📎 {src.type}
                                            </a>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Retry Button / Countdown for Overloaded */}
                                {msg.retry && (
                                    <div className="oracle-retry-section">
                                        {msg.retryDelay && retryCountdown > 0 && idx === messages.length - 1 ? (
                                            <div className="oracle-countdown">
                                                <Clock size={14} className="countdown-icon" />
                                                <span>{t.chatInterface.autoRetry} </span>
                                                <span className="countdown-timer">{retryCountdown}s</span>
                                            </div>
                                        ) : (
                                            <button 
                                                onClick={handleRetry}
                                                className="oracle-retry-btn"
                                                disabled={isLoading}
                                            >
                                                <RefreshCw size={14} />
                                                {t.common.retry}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                        
                        {/* Loading indicator */}
                        {isLoading && (
                            <div className="oracle-message oracle">
                                <div className="oracle-message-header">
                                    <ChevronRight size={14} />
                                    <span>oracle</span>
                                </div>
                                <div className="oracle-message-content oracle-thinking">
                                    <span className="oracle-cursor">█</span>
                                    <span className="oracle-thinking-text">{t.chatInterface.analyzing}</span>
                                </div>
                            </div>
                        )}
                        
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="oracle-input-area">
                        <div className="oracle-input-prompt">
                            <ChevronRight size={16} />
                        </div>
                        <textarea
                            ref={inputRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder={t.chatInterface.placeholder}
                            disabled={isLoading}
                            maxLength={500}
                            className="oracle-input oracle-textarea"
                            rows={3}
                        />
                        <button 
                            onClick={() => sendMessage()}
                            disabled={isLoading || !input.trim()}
                            className="oracle-send-btn"
                        >
                            <Send size={18} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatInterface;

